import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Science,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [perTab, setPerTab] = useState(false);

  // The multi-account ("Keep this login in this window only") checkbox
  // is only meaningful for the dev server hosted at 100.73.138.48 —
  // every other deployment hides it so users don't see a knob they
  // shouldn't be touching.
  const showPerTabCheckbox = typeof window !== 'undefined'
    && window.location.hostname === '100.73.138.48';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user modifies either email or password field
    // This gives immediate feedback that they're addressing the invalid credentials
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password, { perTab });
      if (result.success) {
        // Redirect to the page user was trying to access, or dashboard as default
        const from = location.state?.from;
        const redirectTo = from ? `${from.pathname}${from.search || ''}` : '/';
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Science sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h5">
              Asterism
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sign in to submit observations
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof error === 'object' ? (error.message || JSON.stringify(error)) : error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {showPerTabCheckbox && (
              <Tooltip
                title="Use this when you want two accounts signed in side by side — your session stays in this window only and doesn't sync with other tabs. The session ends when this window is closed."
                placement="right"
                arrow
              >
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={perTab}
                      onChange={(e) => setPerTab(e.target.checked)}
                    />
                  )}
                  label={<Typography variant="body2">Keep this login in this window only (multi-account)</Typography>}
                  sx={{ mt: 1 }}
                />
              </Tooltip>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ mr: 2 }}>
                Forgot your password?
              </Link>
              <br />
              <Link component={RouterLink} to="/register" variant="body2" sx={{ mt: 1, display: 'inline-block' }}>
                {"Don't have an account? Sign Up"}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;